import { Task, TaskPriority, TaskState, TaskHistoryEntry } from '../types';
import { WorkflowManager } from './workflow-manager';
import { TaskSplitter, SplitResult, SubTask } from './task-splitter';
import { logger } from '../utils/logger';
import path from 'path';
import fs from 'fs-extra';
import { exec } from 'child_process';
import { promisify } from 'util';

const execAsync = promisify(exec);

export interface AutoDevOptions {
  projectRoot?: string;
  maxIterations?: number; // Maximum tasks to process (default: unlimited)
  maxDuration?: number; // Maximum time in minutes (default: 24 hours)
  pauseBetweenTasks?: number; // Pause between tasks in seconds (default: 30)
  autoSplit?: boolean; // Automatically split large tasks (default: true)
  autoCommit?: boolean; // Automatically commit after tasks (default: false)
  dryRun?: boolean; // Preview mode without actual execution (default: false)
  logFile?: string; // Log file path
}

export interface AutoDevStatus {
  isRunning: boolean;
  startTime?: Date;
  endTime?: Date;
  totalTasks: number;
  completedTasks: number;
  failedTasks: number;
  currentTask?: string;
  estimatedTimeRemaining?: number;
  logs: string[];
}

export class AutoDev {
  private manager: WorkflowManager;
  private status: AutoDevStatus;
  private abortController?: AbortController;
  private logFile?: string;

  constructor(
    private options: AutoDevOptions = {}
  ) {
    this.manager = new WorkflowManager({
      projectRoot: options.projectRoot || process.cwd()
    });
    
    this.status = {
      isRunning: false,
      totalTasks: 0,
      completedTasks: 0,
      failedTasks: 0,
      logs: []
    };
    
    if (options.logFile) {
      this.logFile = options.logFile;
    }
  }

  /**
   * Start auto development mode
   */
  async start(): Promise<void> {
    if (this.status.isRunning) {
      throw new Error('Auto-dev is already running');
    }

    logger.info('Starting auto-dev mode...');
    this.log('Auto-dev mode started');
    
    this.status.isRunning = true;
    this.status.startTime = new Date();
    this.status.completedTasks = 0;
    this.status.failedTasks = 0;
    this.abortController = new AbortController();

    try {
      const maxIterations = this.options.maxIterations || Infinity;
      const maxDuration = (this.options.maxDuration || 24 * 60) * 60 * 1000; // Convert to ms
      const startTime = Date.now();

      while (
        this.status.isRunning &&
        this.status.completedTasks + this.status.failedTasks < maxIterations &&
        Date.now() - startTime < maxDuration
      ) {
        // Check for abort signal
        if (this.abortController.signal.aborted) {
          this.log('Auto-dev aborted by user');
          break;
        }

        // Get next task
        const nextTask = await this.getNextTask();
        if (!nextTask) {
          this.log('No more tasks available');
          break;
        }

        // Process the task
        await this.processTask(nextTask);

        // Pause between tasks
        if (this.options.pauseBetweenTasks) {
          this.log(`Pausing for ${this.options.pauseBetweenTasks} seconds...`);
          await this.sleep(this.options.pauseBetweenTasks * 1000);
        }
      }

      this.log('Auto-dev mode completed');
    } catch (error) {
      logger.error('Auto-dev error:', error);
      this.log(`Auto-dev error: ${error}`);
    } finally {
      this.status.isRunning = false;
      this.status.endTime = new Date();
      await this.saveStatus();
    }
  }

  /**
   * Stop auto development mode
   */
  async stop(): Promise<void> {
    if (!this.status.isRunning) {
      throw new Error('Auto-dev is not running');
    }

    logger.info('Stopping auto-dev mode...');
    this.log('Auto-dev stop requested');
    
    this.status.isRunning = false;
    this.abortController?.abort();
  }

  /**
   * Get current status
   */
  getStatus(): AutoDevStatus {
    return { ...this.status };
  }

  /**
   * Get next task to work on
   */
  private async getNextTask(): Promise<Task | null> {
    try {
      // First check if there's a current task in progress
      const taskState = await this.manager.getTaskState();
      if (taskState?.current) {
        this.log(`Resuming current task: ${taskState.current.task}`);
        return {
          id: Date.now().toString(),
          content: taskState.current.task,
          priority: taskState.current.priority,
          status: 'in_progress',
          position: 0
        };
      }

      // Get task recommendations
      const recommendations = await this.manager.recommendNextTask();
      if (!recommendations || recommendations.length === 0) {
        return null;
      }

      // Select the highest priority task
      const nextRec = recommendations[0];
      this.log(`Selected next task: ${nextRec.task} (Priority: ${nextRec.priority})`);
      
      return {
        id: Date.now().toString(),
        content: nextRec.task,
        priority: nextRec.priority,
        status: 'pending',
        position: 0
      };
    } catch (error) {
      this.log(`Error getting next task: ${error}`);
      return null;
    }
  }

  /**
   * Process a single task
   */
  private async processTask(task: Task): Promise<void> {
    this.status.currentTask = task.content;
    this.log(`Processing task: ${task.content}`);

    try {
      // Start the task
      await this.manager.startTask({
        task: task.content,
        priority: task.priority,
        phase: 'understanding'
      });

      // Check if task needs to be split
      if (this.options.autoSplit) {
        const estimation = await this.manager.estimateTaskTime(task.content, task.priority);
        const estimatedMinutes = estimation?.complexity?.estimatedTime || 60;
        
        if (estimatedMinutes > 240) { // More than 4 hours
          this.log(`Task is too large (${estimatedMinutes} minutes), splitting...`);
          await this.splitAndProcessTask(task);
          return;
        }
      }

      // Execute task phases
      const phases = ['understanding', 'thinking', 'organizing', 'programming', 'inspecting', 'archiving'] as const;
      
      for (const phase of phases) {
        this.log(`Executing phase: ${phase}`);
        
        if (this.options.dryRun) {
          this.log(`[DRY RUN] Would execute ${phase} phase`);
          await this.sleep(1000); // Simulate work
        } else {
          await this.executePhase(task, phase);
        }

        // Update task state
        const state = await this.manager.getTaskState();
        if (state?.current) {
          state.current.phase = phase;
          await this.manager.saveTaskState(state);
        }
      }

      // Complete the task
      await this.manager.completeCurrentTask(`Auto-completed by auto-dev`);
      this.status.completedTasks++;
      this.log(`Task completed: ${task.content}`);

      // Auto commit if enabled
      if (this.options.autoCommit && !this.options.dryRun) {
        await this.autoCommit(task.content);
      }

    } catch (error) {
      this.status.failedTasks++;
      this.log(`Task failed: ${task.content} - ${error}`);
      
      // Mark task as failed
      const state = await this.manager.getTaskState();
      if (state?.current) {
        const historyEntry: TaskHistoryEntry = {
          task: state.current.task,
          status: 'failed',
          startTime: state.current.startTime,
          endTime: new Date(),
          duration: Date.now() - new Date(state.current.startTime).getTime(),
          notes: `Auto-dev failed: ${error}`,
          phase: state.current.phase
        };
        state.history.push(historyEntry);
        state.current = undefined;
        await this.manager.saveTaskState(state);
      }
    }
    
    this.status.currentTask = undefined;
  }

  /**
   * Split and process a large task
   */
  private async splitAndProcessTask(task: Task): Promise<void> {
    const splitResult = await this.manager.splitTask(task.content, {
      maxHours: 4,
      strategy: 'balanced'
    });

    if (!splitResult) {
      throw new Error('Failed to split task');
    }

    this.log(`Task split into ${splitResult.taskCount} subtasks`);

    // Process subtasks in order
    for (const subtask of splitResult.subtasks) {
      const subTaskObj: Task = {
        id: subtask.id,
        content: subtask.title,
        priority: subtask.priority,
        status: 'pending',
        position: 0
      };

      this.log(`Processing subtask: ${subtask.title}`);
      await this.processTask(subTaskObj);
    }

    // Complete the parent task
    await this.manager.completeCurrentTask(`Auto-completed via ${splitResult.taskCount} subtasks`);
    this.log(`Parent task completed through subtasks`);
  }

  /**
   * Execute a specific phase
   */
  private async executePhase(task: Task, phase: string): Promise<void> {
    // Phase-specific logic
    switch (phase) {
      case 'understanding':
        await this.executeUnderstandingPhase(task);
        break;
      case 'thinking':
        await this.executeThinkingPhase(task);
        break;
      case 'organizing':
        await this.executeOrganizingPhase(task);
        break;
      case 'programming':
        await this.executeProgrammingPhase(task);
        break;
      case 'inspecting':
        await this.executeInspectingPhase(task);
        break;
      case 'archiving':
        await this.executeArchivingPhase(task);
        break;
    }
  }

  /**
   * Understanding phase - analyze the task
   */
  private async executeUnderstandingPhase(task: Task): Promise<void> {
    // Simulate understanding by analyzing the task
    const estimation = await this.manager.estimateTaskTime(task.content, task.priority);
    if (estimation) {
      this.log(`Task complexity: ${estimation.complexity?.score}/100`);
      this.log(`Estimated time: ${estimation.complexity?.estimatedTime} minutes`);
    }
  }

  /**
   * Thinking phase - plan approach
   */
  private async executeThinkingPhase(task: Task): Promise<void> {
    // Could integrate with AI for actual thinking
    this.log('Planning approach for task...');
    await this.sleep(2000);
  }

  /**
   * Organizing phase - prepare resources
   */
  private async executeOrganizingPhase(task: Task): Promise<void> {
    // Check required files, dependencies, etc.
    this.log('Organizing resources...');
    await this.sleep(1000);
  }

  /**
   * Programming phase - implement solution
   */
  private async executeProgrammingPhase(task: Task): Promise<void> {
    // This is where actual coding would happen
    // In a real implementation, this would call Claude Code or other AI
    this.log('Implementing solution...');
    await this.sleep(5000);
  }

  /**
   * Inspecting phase - test and verify
   */
  private async executeInspectingPhase(task: Task): Promise<void> {
    // Run tests, linting, type checking
    this.log('Running tests and verification...');
    
    if (!this.options.dryRun) {
      try {
        // Run linting
        await execAsync('pnpm lint', { cwd: this.options.projectRoot });
        this.log('Linting passed');
      } catch (error) {
        this.log(`Linting failed: ${error}`);
      }

      try {
        // Run type checking
        await execAsync('pnpm type-check', { cwd: this.options.projectRoot });
        this.log('Type checking passed');
      } catch (error) {
        this.log(`Type checking failed: ${error}`);
      }
    }
  }

  /**
   * Archiving phase - commit and document
   */
  private async executeArchivingPhase(task: Task): Promise<void> {
    // Create documentation, update changelog
    this.log('Archiving and documenting...');
    await this.sleep(1000);
  }

  /**
   * Auto commit changes
   */
  private async autoCommit(taskDescription: string): Promise<void> {
    try {
      // Check if there are changes
      const { stdout: status } = await execAsync('git status --porcelain', {
        cwd: this.options.projectRoot
      });

      if (!status.trim()) {
        this.log('No changes to commit');
        return;
      }

      // Add all changes
      await execAsync('git add -A', { cwd: this.options.projectRoot });

      // Create commit message
      const message = `feat: ${taskDescription}\\n\\nAuto-committed by workflow auto-dev`;
      await execAsync(`git commit -m "${message}"`, { cwd: this.options.projectRoot });
      
      this.log('Changes committed automatically');
    } catch (error) {
      this.log(`Auto commit failed: ${error}`);
    }
  }

  /**
   * Log message
   */
  private log(message: string): void {
    const timestamp = new Date().toISOString();
    const logEntry = `[${timestamp}] ${message}`;
    
    this.status.logs.push(logEntry);
    logger.info(message);

    // Write to file if configured
    if (this.logFile) {
      fs.appendFileSync(this.logFile, logEntry + '\n');
    }
  }

  /**
   * Sleep helper
   */
  private async sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Save status to file
   */
  private async saveStatus(): Promise<void> {
    try {
      const statusFile = path.join(
        this.options.projectRoot || process.cwd(),
        '.workflow',
        'auto-dev-status.json'
      );
      
      await fs.ensureDir(path.dirname(statusFile));
      await fs.writeJson(statusFile, this.status, { spaces: 2 });
    } catch (error) {
      logger.error('Failed to save auto-dev status:', error);
    }
  }

  /**
   * Load previous status
   */
  async loadStatus(): Promise<AutoDevStatus | null> {
    try {
      const statusFile = path.join(
        this.options.projectRoot || process.cwd(),
        '.workflow',
        'auto-dev-status.json'
      );
      
      if (await fs.pathExists(statusFile)) {
        return await fs.readJson(statusFile);
      }
    } catch (error) {
      logger.error('Failed to load auto-dev status:', error);
    }
    
    return null;
  }
}